import { all, put, select, race, take, takeEvery } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getToken, getLocale, getCustomData } from '@global/selectors'

import { blockSaveCustomData } from '../../../../../../../components/Workspace/components/HocBlock/actions'
import config from '../../../../../../../../../config'

import {
    NEGATIVE_EVENTS_LOAD_TABLE_DATA,
    NEGATIVE_EVENTS_TABLE_CHANGE,
    NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST,
    NEGATIVE_EVENTS_TABLE_LOADING_TRUE,
    NEGATIVE_EVENTS_TABLE_LOADING_FALSE,
    NEGATIVE_EVENTS_TABLE_DATA_RECEIVED,
    NEGATIVE_EVENTS_LOAD_EVENT_DETAIL,
    NEGATIVE_EVENTS_LOAD_EVENT_DETAIL_REQUEST_SUCCESS,
    NEGATIVE_EVENTS_LOAD_EVENT_DETAIL_REQUEST_FAILED,
    NEGATIVE_EVENTS_LOAD_EVENT_DETAIL_RECEIVED,
    NEGATIVE_EVENTS_LOAD_DISTRAINTS_HISTORY,
    NEGATIVE_EVENTS_LOAD_DISTRAINTS_HISTORY_REQUEST,
} from './constants'

import { getUserFirstName, getUserLastName } from './selectors'

import {
    negativeEventsLoadTableDataRequest,
    negativeEventsTableLoadingTrue,
    negativeEventsTableLoadingFalse,
    negativeEventsTableDataReceived,
    negativeEventsLoadEventDetailRequest,
    negativeEventsLoadEventDetailReceived,
    negativeEventsLoadDistraintsHistoryRequest,
} from './actions'

const NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST_SUCCESS = (tableId) =>
    `${NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST(tableId)}_${SUCCESS}`
const NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST_FAILED = (tableId) =>
    `${NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST(tableId)}_${FAILED}`

function* negativeEventsTablesLoadCallSaga(action) {
    const block = _.get(action, 'data.block', null)
    if (block !== config.modules.report.blocks.negativeEvents.key) return null

    const entId = _.get(action, 'data.entId', null)
    const module = _.get(action, 'data.module', null)
    if (!module || !entId || !block) return null

    const tableId = _.get(action, 'data.tableId', null)
    const tableSelector = _.get(action, 'data.tableSelector', null)
    const level = _.get(action, 'data.level', null)
    const customData = _.get(action, 'data.customData', null)
    const locale = yield select(getLocale)
    const token = yield select(getToken, entId, module)

    const lang = config.languages[locale].requestKey

    yield put(
        negativeEventsTableLoadingTrue(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: true,
                dataLocale: null,
            },
        })
    )

    yield put(
        negativeEventsLoadTableDataRequest(
            tableId,
            tableSelector,
            entId,
            module,
            block,
            level,
            lang,
            token,
            _.get(config, `api.${module}.${block}.tables.${tableSelector}.qs`, {})
        )
    )

    const response = yield race({
        success: take(NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST_SUCCESS(tableId)),
        failed: take(NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST_FAILED(tableId)),
    })

    if (!response.failed) {
        const responseBody = _.get(response, 'success.payload.response.body', {})

        yield put(
            negativeEventsTableDataReceived(module, entId, block, {
                [tableId]: {
                    isLoading: false,
                    loaded: true,
                    failed: false,
                    dataLocale: locale,
                    data: _.get(responseBody, 'rows', []),
                    pagination: _.get(responseBody, 'query.pagination', {}),
                    sorted: _.reduce(
                        _.get(responseBody, 'query.sort.sortedColumns'),
                        (acc, val) => [
                            ...acc,
                            {
                                id: _.get(val, 'columm'), // TYPO FROM BACKEND
                                desc: _.get(val, 'direction') === 'desc',
                            },
                        ],
                        []
                    ),
                    filter: _.get(responseBody, 'query.filter', {}),
                },
            })
        )

        return null
    }
    yield put(
        negativeEventsTableLoadingFalse(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: false,
                loaded: true,
                failed: true,
            },
        })
    )

    return null
}

function* negativeEventsTableChangeCallSaga(action) {
    const block = _.get(action, 'data.block', null)
    if (block !== config.modules.report.blocks.negativeEvents.key) return null

    const entId = _.get(action, 'data.entId', null)
    const module = _.get(action, 'data.module', null)
    if (!module || !entId || !block) return null

    const tableId = _.get(action, 'data.tableId', null)
    const tableSelector = _.get(action, 'data.tableSelector', null)
    const level = _.get(action, 'data.level', null)
    const customData = _.get(action, 'data.customData', null)
    const queryParams = _.get(action, 'data.queryParams', null)
    const locale = yield select(getLocale)
    const token = yield select(getToken, entId, module)

    const lang = config.languages[locale].requestKey

    const filters = _.reduce(
        queryParams?.filtered,
        (acc, filter) => ({
            ...acc,
            [`${filter.id}[]`]: filter.value,
        }),
        {}
    )

    const qp = {
        'sort[]': _.map(_.get(queryParams, 'sorted', []), (val) => `${_.get(val, 'id')}${val.desc ? ':desc' : ':asc'}`),
        page: _.get(queryParams, 'pagination.page'),
        rows: _.get(queryParams, 'pagination.rowsPerPage'),
        ...filters,
    }

    yield put(
        negativeEventsTableLoadingTrue(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: true,
            },
        })
    )

    yield put(negativeEventsLoadTableDataRequest(tableId, tableSelector, entId, module, block, level, lang, token, qp))

    const response = yield race({
        success: take(NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST_SUCCESS(tableId)),
        failed: take(NEGATIVE_EVENTS_LOAD_TABLE_DATA_REQUEST_FAILED(tableId)),
    })

    if (!response.failed) {
        const responseBody = _.get(response, 'success.payload.response.body', {})

        yield put(
            negativeEventsTableDataReceived(module, entId, block, {
                [tableId]: {
                    isLoading: false,
                    loaded: true,
                    dataLocale: locale,
                    data: _.get(responseBody, 'rows', []),
                    pagination: _.get(responseBody, 'query.pagination', {}),
                    sorted: _.reduce(
                        _.get(responseBody, 'query.sort.sortedColumns'),
                        (acc, val) => [
                            ...acc,
                            {
                                id: _.get(val, 'columm'), // TYPO FROM BACKEND
                                desc: _.get(val, 'direction') === 'desc',
                            },
                        ],
                        []
                    ),
                    filter: _.get(responseBody, 'query.filter', {}),
                },
            })
        )

        return null
    }

    yield put(
        negativeEventsTableLoadingFalse(module, block, entId, {
            [tableId]: {
                ..._.get(customData, `${tableId}`, {}),
                isLoading: false,
                loaded: true,
            },
        })
    )

    return null
}

function* negativeEventsSaveCustomDataCallSaga(action) {
    const blockKey = _.get(action, 'data.block', null)
    if (blockKey !== config.modules.report.blocks.negativeEvents.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    if (!module || !id || !blockKey) return null

    const customData = yield select(getCustomData, module, id, blockKey)
    const incomingChange = _.get(action, 'data.customData', {})

    yield put(blockSaveCustomData(module, id, blockKey, { ...customData, ...incomingChange }))

    return null
}

function* negativeEventsLoadEventDetailCallSaga(action) {
    const { module, block, entId, docId, level } = action.payload
    const token = yield select(getToken, entId, module)

    yield put(negativeEventsLoadEventDetailRequest({ entId, docId, token, level, block, module }))

    const response = yield race({
        success: take(NEGATIVE_EVENTS_LOAD_EVENT_DETAIL_REQUEST_SUCCESS),
        failed: take(NEGATIVE_EVENTS_LOAD_EVENT_DETAIL_REQUEST_FAILED),
    })

    if (!response.failed) {
        const customData = yield select(getCustomData, module, block, entId)
        const eventDetailData = _.get(response, 'success.payload.response.body', {})

        yield put(
            negativeEventsLoadEventDetailReceived({
                module,
                entId,
                block,
                customData: { ...customData, eventsDetail: { ...customData?.eventsDetail, [docId]: eventDetailData } },
            })
        )
    }
}

function* negativeEventsLoadDistraintsHistoryCallSaga(action) {
    const { id, module, block } = action.data
    const firstName = yield select(getUserFirstName)
    const lastName = yield select(getUserLastName)

    yield put(negativeEventsLoadDistraintsHistoryRequest())

    const result = yield race({
        success: take(`${NEGATIVE_EVENTS_LOAD_DISTRAINTS_HISTORY_REQUEST}_SUCCESS`),
        failed: take(`${NEGATIVE_EVENTS_LOAD_DISTRAINTS_HISTORY_REQUEST}_FAILED`),
    })

    if (!result.failed) {
        let data = null
        const latestHistoryItem = result.success.payload.response.body?.data?.history?.[0]

        if (latestHistoryItem) {
            data = {
                distraintsHistory: {
                    ...latestHistoryItem,
                    name: `${firstName} ${lastName}`,
                },
            }
        }

        yield put(blockSaveCustomData(module, id, block, data))
    }
}

export default function* negativeEventsSaga() {
    yield all([
        takeEvery(NEGATIVE_EVENTS_LOAD_TABLE_DATA, negativeEventsTablesLoadCallSaga),
        takeEvery(NEGATIVE_EVENTS_TABLE_LOADING_TRUE, negativeEventsSaveCustomDataCallSaga),
        takeEvery(NEGATIVE_EVENTS_TABLE_LOADING_FALSE, negativeEventsSaveCustomDataCallSaga),
        takeEvery(NEGATIVE_EVENTS_TABLE_DATA_RECEIVED, negativeEventsSaveCustomDataCallSaga),
        takeEvery(NEGATIVE_EVENTS_LOAD_EVENT_DETAIL_RECEIVED, negativeEventsSaveCustomDataCallSaga),
        takeEvery(NEGATIVE_EVENTS_TABLE_CHANGE, negativeEventsTableChangeCallSaga),
        takeEvery(NEGATIVE_EVENTS_LOAD_EVENT_DETAIL, negativeEventsLoadEventDetailCallSaga),
        takeEvery(NEGATIVE_EVENTS_LOAD_DISTRAINTS_HISTORY, negativeEventsLoadDistraintsHistoryCallSaga),
    ])
}
